import React, { useCallback } from "react";
import { useController } from "./useController";
import { Button, Card } from "@components";
import { SearchClient, PaymentSuccess, RecapCard, MultiPaymentSelector, PromotionSection } from "./components";
import { PageWrapper } from "@connectedComponents";
import { useInvoiceDownloader } from "@utilities/hooks";

export const Payment: React.FC = () => {

    const { downloadInvoice } = useInvoiceDownloader();

    const {
        selectedSale, clientTotalPay, totalToPay,
        paymentMethods, totalBasket,
        activeBasket, clients, stepIndex,
        getOnePromotionError, isPaymentComplete,
        selectedClient, createClientSuccess,
        selectedPromotion, handleApplyPromotion, handleRemovePromotion,
        discountAmount, discountedTotal,
        onShopRedirect, onAddPayment, onRemovePayment, onUpdatePayment,
        setSelectedClient, setCreateClientSuccess,
        handleGoBack, onFindClient, onCreateClient, handleGoToNextStep,
    } = useController();

    const renderRecapCard = useCallback((index: number, buttonName = 'Suivant') => (
        <div className="flex-1 flex flex-col gap-4">
            <RecapCard
                basket={activeBasket?.basket!}
                totalBasket={totalBasket}
                promotion={selectedPromotion}
                discountAmount={discountAmount}
                discountedTotal={discountedTotal}
            />

            <PromotionSection
                error={getOnePromotionError}
                promotion={selectedPromotion}
                totalToPay={totalBasket}
                onApplyPromotion={handleApplyPromotion}
                onRemovePromotion={handleRemovePromotion}
            />

            <Button
                variant="secondary"
                className="rounded-btn"
                onClick={() => handleGoToNextStep(index)}
            >
                {buttonName}
            </Button>
        </div>
    ), [
        activeBasket, totalBasket,
        getOnePromotionError, selectedPromotion,
        discountAmount, discountedTotal,
        handleApplyPromotion, handleRemovePromotion, handleGoToNextStep,
    ]);

    const renderClientSection = useCallback(() => (
        <div className="flex flex-col md:flex-row md:items-start gap-4">
            <Card
                title="Rechercher un client par son nom, prénom, numéro de téléphone ou adresse e-mail"
                customClassName="w-full md:w-1/2"
            >
                <SearchClient
                    selectedClient={selectedClient}
                    suggestions={clients}
                    onFindClient={onFindClient}
                    onSelect={setSelectedClient}
                    onCreateClient={onCreateClient}
                    onClearClient={() => setSelectedClient(null)}
                />
            </Card>

            {renderRecapCard(1)}
        </div>
    ), [
        selectedClient, clients,
        onFindClient, setSelectedClient,
        onCreateClient, renderRecapCard,
    ]);

    const renderPaymentMethods = useCallback(() => (
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
            <Card
                title="Modes de paiement"
                customClassName="w-full lg:w-2/3"
            >
                <MultiPaymentSelector
                    totalToPay={discountedTotal}
                    paymentMethods={paymentMethods}
                    selectedClient={selectedClient}
                    onAddPayment={onAddPayment}
                    onRemovePayment={onRemovePayment}
                    onUpdatePayment={onUpdatePayment}
                />
            </Card>

            <div className="flex-1 flex flex-col gap-4 lg:w-1/3">
                <RecapCard
                    basket={activeBasket?.basket!}
                    totalBasket={totalBasket}
                    paymentMethods={paymentMethods}
                    promotion={selectedPromotion}
                    discountAmount={discountAmount}
                    discountedTotal={discountedTotal}
                />

                <Button
                    variant="secondary"
                    className="rounded-btn"
                    onClick={() => handleGoToNextStep(2)}
                    disabled={!isPaymentComplete}
                >
                    {isPaymentComplete ? 'Confirmer le paiement' : 'Complétez le paiement'}
                </Button>
            </div>
        </div>

    ), [
        discountedTotal, paymentMethods, selectedClient,
        onAddPayment, onRemovePayment, onUpdatePayment,
        activeBasket, totalBasket, isPaymentComplete, handleGoToNextStep,
        selectedPromotion, discountAmount,
    ]);

    const renderSaleStatus = useCallback(() => {
        if (!selectedSale) {
            return (
                <Card>
                    <div className="flex flex-col items-center justify-center w-full h-full">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
                        <p className="text-gray-600 text-center mb-6">
                            Une erreur s'est produite lors de la création de la vente.
                        </p>

                        <Button
                            variant="secondary"
                            className="rounded-btn"
                            onClick={onShopRedirect}
                        >
                            Retour à la boutique
                        </Button>
                    </div>
                </Card>
            )
        };

        return (
            <PaymentSuccess
                orderNumber={selectedSale.orderNumber}
                onDownload={() => downloadInvoice(selectedSale!)}
                onPrint={() => downloadInvoice(selectedSale!, true)}
                onShopRedirect={onShopRedirect}
                onSellingManagementRedirect={() => alert('Pas encore disponible')}
            />
        )
    }, [selectedSale, downloadInvoice, onShopRedirect]);

    const renderContent = useCallback(() => {
        switch (stepIndex) {
            case 0:
                return renderClientSection();
            case 1:
                return renderPaymentMethods();
            case 2:
                return renderSaleStatus();
            default:
                return;
        }
    }, [
        stepIndex, renderSaleStatus,
        renderClientSection, renderPaymentMethods,
    ]);

    return (
        <PageWrapper
            activeStack="shopStack"
            handleGoBack={handleGoBack}
        >
            {renderContent()}
        </PageWrapper >
    );
};
